// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Classes for building a rectilinear polygons in stages including basic
 * rectangles.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Edge } from "./Edge.js"
import { Polygon } from "./Polygon.js"
import { Vector2D } from "./Vector.js"

// @@imports-utils
import { DoubleLinkedList, DoubleLinkedListNode } from "../../data/index.js"
import { DecoratedError } from "../../logging/index.js"
import { BitBuffer } from "../../data/index.js" 

// @@body
/**
 * Polygon with all internal angles being multiples of PI/2 radians (90
 * degrees), and all edges grid aligned (i.e. edges which are parallel to the
 * horizontal or vertical axis). This implementation also provides:
 * - Expected method overrides from base class.
 * - Methods to move and resize any edge.
 * - Methods to calculate border areas.
 * - Methods to split polygon into a set of rectangles.
 * @todo Finish documentation.
 * @todo Implement add rectangle method to match subtract rectangle method.
 */
class RectilinearPolygon extends Polygon {
    #holes = /** @type {Map.<Rectangle, Rectangle>} */ (new Map())

    /**
     * Construct rectilinear polygon from initial set of vertices.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D[]} [obj.vertices] Existing vertices at point of
     * instantiation.
     * @param {boolean} [obj.isClosed] Is polygon closed at point of
     * instantiation?
     */
    constructor({ vertices = [new Vector2D(0, 0)], isClosed = false } = {}) {
        super()

        // Construct polygon from existing data passed in constructor.
        for (const vertex of vertices) { this.addVertex(vertex) }
        if (isClosed) { this.#closePolygon() }
    }

    /**
     * Add a new vertex in sequence to the end the current instance's array of
     * vertices. Will also add a new edge required to join this new vertex to
     * the existing polygon curve.
     * @param {Vector2D} vertex Vertex to add to current polygon instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.dissolveVertices] Should method dissolve
     * redundant vertices at the end of the add operation.
     * @returns {void}
     * @throws {RectilinearPolygonError} Throws when:
     * - A vertex with identical coordinates already exists in the polygon.
     * Or when Generated edge *from* previous vertex *to* new vertex is:
     * - Not grid aligned.
     * - Has *zero* magnitude.
     * - Intersects any edge other than specifically the `end` vertex *only* of
     * the previous edge.
     */
    addVertex(vertex, { dissolveVertices = true } = {}) {
        // Throw if current polygon instance is closed (cannot add vertices).
        if (this.isClosed) {
            throw new RectilinearPolygonError({
                message: "Cannot add vertices to a closed polygon instance",
                fix: "Ensure polygon is not closed before adding vertices"
            })
        }

        // Handle case where no vertices exist in polygon, and therefore grid
        // alignment checks etc. are irrelevant since there are no edges.
        const vertexNode = new DoubleLinkedListNode(vertex)
        if (!this.vertices.size) { return this.vertices.addNode(vertexNode) }

        // Handle case for vertex which should close the current polygon (i.e.
        // when added vertex equals the `head` vertex of the internal list).
        const headVertex = this.vertices.head?.value
        if (headVertex && vertex.equals(headVertex)) {
            return this.#closePolygon()
        }

        // Throw if a vertex with identical coordinates already exists in the
        // current polygon instance (special case of closing vertex handled
        // above).
        for (const { value: existingVertex } of this.vertices) {
            if (vertex.equals(existingVertex)) {
                throw new RectilinearPolygonError({
                    message: "Identical vertex already exists in polygon",
                    fix: "Do not add duplicate vertices"
                })
            }
        }

        // Fetch previous vertex and edge node values. Throw if previous vertex
        // does not exist.
        const previousVertex = this.vertices.tail?.value
        if (!previousVertex) {
            throw new RectilinearPolygonError({
                message: "Previous vertex not found",
                fix: "Check that internal linked lists have not been modified"
            })
        }

        // Generate edge and list edge node from new and previous vertices.
        const edge = new Edge(previousVertex, vertex)
        const edgeNode = new DoubleLinkedListNode(edge)

        // Throw if generated edge is not grid aligned.
        if (!edge.isGridAligned) {
            throw new RectilinearPolygonError({
                message: "Generated edge is not grid aligned",
                fix: "Check vertex is grid aligned with previous vertex"
            })
        }

        // Throw if new edge intersects any existing edge.
        for (const { value: existingEdge } of this.edges) {
            if (edge.intersects(existingEdge)) {
                // Ignore intersection if it is specifically the *end* vertex
                // *only* of the previous edge.
                const intersection = edge.getIntersection(existingEdge)
                if (
                    intersection?.equals(edge.start) ||
                    edge.direction === existingEdge.direction
                ) { continue }

                throw new RectilinearPolygonError({
                    message: "Generated edge intersects an existing edge",
                    fix: "Check vertex does not cause new edge to cross others"
                })
            }
        }

        // Add generated vertex and edge nodes, and dissolve redundant vertices.
        this.vertices.addNode(vertexNode)
        this.edges.addNode(edgeNode)
        if (dissolveVertices) { this.dissolveVertices() }
    }

    /**
     * Move an existing edge based on an updated start vertex. Note that this
     * method will:
     * - Only move edge if operation respects constraints of a rectilinear
     * polygon (i.e. all 90 degree angles and no edges intersecting).
     * - Update surrounding edges as required to maintain a continuous curve.
     * @param {Edge} edge Edge to move.
     * @param {Vector2D} updatedStart Required new start vertex of edge.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.dissolveVertices] Should method dissolve
     * redundant vertices at the end of the move operation.
     * @throws {RectilinearPolygonError} Throws when:
     * - Edge is not contained in current polygon instance.
     * - Edge is not being moved *perpendicular* to edge direction.
     * - Updated edges required to meet new updated start vertex intersect other
     * existing edges in the polygon.
     */
    moveEdge(edge, updatedStart, { dissolveVertices = true } = {}) {
        // Ignore if edge already starts at required start vertex.
        if (updatedStart.equals(edge.start)) { return }

        // Fetch edge node by value. Throw if node does not resolve to a node
        // contained in the polygon edges linked list.
        const node = this.edges.nodeWithValue(edge)
        if (!node) {
            throw new RectilinearPolygonError({
                message: "Edge node with given value not found in polygon",
                fix: "Move edge which exists in polygon"
            })
        }
        let index = this.edges.indexOfNode(node)

        // Throw if edge is not being moved perpendicular to edge direction.
        if (edge.angleTo(updatedStart.subtract(edge.start)) !== Math.PI / 2) {
            throw new RectilinearPolygonError({
                message: "Cannot move edge in that direction",
                fix: "Ensure only moving edge perpendicular to edge direction"
            })
        }

        // 
        const {
            previous: previousEdgeNode,
            next: nextEdgeNode
        } = node
        const previousStart = previousEdgeNode &&
            previousEdgeNode.value.direction !== edge.direction
                ? previousEdgeNode.value.start
                : edge.start.copy()
        const nextEnd = nextEdgeNode &&
            nextEdgeNode.value.direction !== edge.direction
                ? nextEdgeNode?.value.end
                : edge.end

        const dividerCoord = edge.isVertical ? edge.start.x : edge.start.y
        const deltaStart = edge.isVertical
            ? updatedStart.x - dividerCoord
            : updatedStart.y - dividerCoord
        const {
            minDeltaStart,
            maxDeltaStart
        } = this.edges.array.reduce((acc, cur) => {
            if (cur === edge || !cur.isParallelTo(edge)) { return acc }

            const vertexCoord = cur.isVertical ? cur.start.x : cur.start.y
            const deltaStart = vertexCoord - dividerCoord

            const movedStartVertex = edge.isVertical
                ? new Vector2D(edge.start.x + deltaStart, edge.start.y)
                : new Vector2D(edge.start.x, edge.start.y + deltaStart)
            const movedEndVertex = edge.isVertical
                ? new Vector2D(edge.end.x + deltaStart, edge.end.y)
                : new Vector2D(edge.end.x, edge.end.y + deltaStart)
            const movedEdge = new Edge(movedStartVertex, movedEndVertex)

            const end = movedEdge.getParallelIntersectionFractions(cur)?.end
            if (
                movedEdge.getParallelIntersectionMagnitude(cur) ||
                (cur !== previousEdgeNode?.previous?.value && end === 0) ||
                (cur !== nextEdgeNode?.next?.value && end === 1)
            ) {
                acc.minDeltaStart = deltaStart < 0
                    ? Math.max(acc.minDeltaStart, deltaStart)
                    : acc.minDeltaStart
                acc.maxDeltaStart = deltaStart > 0
                    ? Math.min(acc.maxDeltaStart, deltaStart)
                    : acc.maxDeltaStart
            }

            return acc
        }, { minDeltaStart: - Infinity, maxDeltaStart: Infinity })

        // 
        if (deltaStart <= minDeltaStart || deltaStart >= maxDeltaStart) {
            throw new RectilinearPolygonError({
                message: "Updated edges intersect existing edges",
                fix: "Do not move edge which causes intersection"
            })
        }

        // 
        let deltaHole = Infinity
        for (const [rectangle] of this.#holes) {
            // 
            const anchorCoord = edge.isVertical
                ? rectangle.anchor.x
                : rectangle.anchor.y
            const deltaRect = anchorCoord - dividerCoord
            if (Math.sign(deltaStart) !== Math.sign(deltaRect)) { continue }

            // 
            for (const { value: rectangleEdge } of rectangle.edges) {
                const deltaEdge = edge.getDistanceToEdge(
                    rectangleEdge,
                    { forcePerpendicular: true }
                )
                if (!isNaN(deltaEdge)) {
                    deltaHole = Math.min(deltaHole, deltaEdge)
                }
            }
        }
        if (Math.abs(deltaStart) >= deltaHole) {
            throw new RectilinearPolygonError({
                message: "Updated edges intersect internal hole",
                fix: "Do not move edge which causes intersection"
            })
        }

        // 
        const updatedEnd = updatedStart.add(edge)
        const updatedEdges = {
            previous: new Edge(previousStart, updatedStart),
            next: new Edge(updatedEnd, nextEnd)
        }

        // 
        let vertexNode = new DoubleLinkedListNode(previousStart)
        let edgeNode = new DoubleLinkedListNode(updatedEdges.previous)
        if (previousEdgeNode) {
            if (previousEdgeNode.value.direction !== edge.direction) {
                const { start, x, y } = updatedEdges.previous
                previousEdgeNode.value.set({ start, x, y })
            }
            else {
                this.vertices.addNode(vertexNode, { parentNode: index - 1 })
                this.edges.addNode(edgeNode, { parentNode: index - 1 })
                index++
            }
        }
        else if (!previousEdgeNode) {
            this.vertices.addNode(vertexNode, { parentNode: null })
            this.edges.addNode(edgeNode, { parentNode: null })
            index++
        }

        // 
        vertexNode = new DoubleLinkedListNode(updatedEdges.next.start)
        edgeNode = new DoubleLinkedListNode(updatedEdges.next)
        if (nextEdgeNode) {
            if (nextEdgeNode.value.direction !== edge.direction) {
                const { start, x, y } = updatedEdges.next
                nextEdgeNode.value.set({ start, x, y })
            }
            else {
                this.vertices.addNode(vertexNode, { parentNode: index })
                this.edges.addNode(edgeNode, { parentNode: index })
            }
        }
        else if (!nextEdgeNode) {
            this.vertices.addNode(vertexNode, { parentNode: - 2 })
            this.edges.addNode(edgeNode)
        }

        // 
        edge.set({ start: updatedStart })
        if (dissolveVertices) { this.dissolveVertices() }
    }

    /**
     * Resize an existing edge based on an updated magnitude. Note that this
     * method will:
     * - Translate all vertices and edges below or to the right of the edge
     * being resized in order to maintain shape of remainder of polygon.
     * - Resize *all* edges which are parallel to and in line with resized edge
     * in order to maintain existing connections between the translated and
     * non-translated halves of the polygon.
     * @param {Edge} edge Edge to resize.
     * @param {number} updatedMagnitude Required new magnitude of edge.
     * @param {object} obj Optional destructured parameters object.
     * @param {Edge} [obj.divider] Dividing edge below (or right of) which all
     * edges and vertices should be translated in order to resize edge whilst
     * maintaining shape of remaining polygon. Defaults to edge perpendicular
     * to `end` vertex of edge being resized.
     * @param {boolean} [obj.dissolveVertices] Should method dissolve
     * redundant vertices at the end of the resize operation.
     * @throws {RectilinearPolygonError} Throws when:
     * - Edge node to resize not found in polygon.
     * - Dividing edge is not perpendicular to the edge being resized.
     * - Resize causes other edges in polygon to intersect.
     */ 
    resizeEdge(edge, updatedMagnitude, {
        divider,
        dissolveVertices = true
    } = {}) {
        // Ignore if edge already has required magnitude.
        const deltaMagnitude = updatedMagnitude - edge.magnitude
        if (deltaMagnitude === 0) { return }
        const deltaVector = edge.isVertical
            ? new Vector2D(0, deltaMagnitude)
            : new Vector2D(deltaMagnitude, 0)
        if (edge.direction === Math.PI || edge.direction === - Math.PI / 2) {
            deltaVector.scalarMultiply(- 1, { inPlace: true })
        }

        // Fetch edge node by value. Throw if node does not resolve to a node
        // contained in the polygon edges linked list.
        const node = this.edges.nodeWithValue(edge)
        if (!node) {
            throw new RectilinearPolygonError({
                message: "Edge node with given value not found in polygon",
                fix: "Resize edge which exists in polygon"
            })
        }

        // 
        // const { startCoord, endCoord } = edge.isVertical
        //     ? { startCoord: edge.start.y, endCoord: edge.end.y }
        //     : { startCoord: edge.start.x, endCoord: edge.end.x }
        // const end = startCoord < endCoord ? edge.end : edge.start
        // const { anchor: { x, y }, width, height } = this.boundingBox
        // divider ??= edge.isVertical
        //     ? new Edge(new Vector2D(x, end.y), new Vector2D(x + width, end.y))
        //     : new Edge(new Vector2D(end.x, y), new Vector2D(end.x, y + height))
        // const dividerCoord = edge.isVertical ? divider.end.y : divider.end.x

        // 
        // if (edge.angleTo(divider) !== Math.PI / 2) {
        //     throw new RectilinearPolygonError({
        //         message: "Dividing edge is not perpendicular to resized edge",
        //         fix: "Ensure dividing edge is perpendicular to resized edge"
        //     })
        // }

        // 
        // const { minDeltaMagnitude } = this.vertices.array.reduce((acc, cur) => {
        //     const vertexCoord = divider?.isVertical ? cur.x : cur.y

        //     if (
        //         vertexCoord < dividerCoord &&
        //         vertexCoord > acc.maxPreDividerCoord
        //     ) { acc.maxPreDividerCoord = vertexCoord }

        //     acc.minDeltaMagnitude = acc.maxPreDividerCoord - dividerCoord

        //     return acc
        // }, { maxPreDividerCoord: - Infinity, minDeltaMagnitude: Infinity })

        // // 
        // if (deltaMagnitude < minDeltaMagnitude) {
        //     throw new RectilinearPolygonError({
        //         message: "Resize causes existing edges to intersect",
        //         fix: "Resize edge to larger value"
        //     })
        // }

        // let deltaHole = Infinity
        // for (const [rectangle] of this.#holes) {
        //     const {
        //         anchor,
        //         rotation,
        //         edges: { array: rectangleEdges }
        //     } = rectangle
        //     const anchorCoord = edge.isVertical ? anchor.y : anchor.x
        //     if (anchorCoord > dividerCoord) { continue }

        //     let rectangleEdgeIndexes = rotation < Math.PI / 2 ? [0, 1]
        //         : rotation < Math.PI ? [0, 3]
        //         : rotation < 3 * Math.PI / 2 ? [2, 3]
        //         : [1, 2]
        //     if (edge.isVertical) {
        //         rectangleEdgeIndexes = rectangleEdgeIndexes.map(index => {
        //             return (index + 1) % 4
        //         })
        //     }

        //     for (const { value: instanceEdge } of this.edges) {
        //         if (instanceEdge.isParallelTo(edge)) { continue }

        //         const instanceCoord = edge.isVertical
        //             ? instanceEdge.start.y
        //             : instanceEdge.start.x
        //         if (instanceCoord < anchorCoord) { continue }

        //         for (const index of rectangleEdgeIndexes) {
        //             const rectangleEdge = rectangleEdges[index]
        //             const deltaEdge = instanceEdge.getDistanceToEdge(
        //                 rectangleEdge,
        //                 { forcePerpendicular: true }
        //             )
        //             if (!isNaN(deltaEdge)) {
        //                 deltaHole = Math.min(deltaHole, deltaEdge)
        //             }
        //         }
        //     }
        // }
        // if (Math.abs(deltaMagnitude) >= deltaHole) {
        //     throw new RectilinearPolygonError({
        //         message: "Resize causes existing edges to intersect with hole",
        //         fix: "Resize edge to larger value"
        //     })
        // }

        // 
        // for (const { value: polyEdge } of this.edges) {
        //     const { direction } = polyEdge
        //     const { startCoord, endCoord } = edge.isVertical
        //         ? { startCoord: polyEdge.start.y, endCoord: polyEdge.end.y }
        //         : { startCoord: polyEdge.start.x, endCoord: polyEdge.end.x }

        //     if (startCoord >= dividerCoord || endCoord >= dividerCoord) {
        //         if (
        //             !(startCoord >= dividerCoord && endCoord >= dividerCoord) &&
        //             polyEdge.isParallelTo(edge) &&
        //             polyEdge.intersects(divider)
        //         ) {
        //             if (direction === 0 || direction === Math.PI / 2) {
        //                 polyEdge.add(deltaVector, { inPlace: true })
        //                 polyEdge.start.subtract(deltaVector, { inPlace: true })
        //             }
        //             else {
        //                 polyEdge.subtract(deltaVector, { inPlace: true })
        //             }
        //         }

        //         polyEdge.start.add(deltaVector, { inPlace: true })
        //     }
        // }

        const edgeIndex = this.edges.indexOfValue(edge)
        const edges = this.edges
            .traverse((edgeIndex + 1) % this.edges.size, {
                endNode: edgeIndex,
                inclusive: false
            })
        edge.add(deltaVector, { inPlace: true })
        for (const { value: nextEdge } of edges) {
            nextEdge.start.add(deltaVector, { inPlace: true })
            if (nextEdge.isParallelTo(edge)) {
                // 
                let fragment = deltaVector.copy()
                if (nextEdge.direction === deltaVector.direction) {
                    const delta = Math.round(Math.min(
                        nextEdge.magnitude - 10,
                        deltaVector.magnitude
                    ))
                    fragment = deltaVector.normalize().scalarMultiply(delta)
                }

                // 
                nextEdge.subtract(fragment, { inPlace: true })
                deltaVector.subtract(fragment, { inPlace: true })
                if (deltaVector.magnitude === 0) { break }
            }
        }

        if (this.boundingBox.anchor.magnitude) {
            this.translate(this.boundingBox.anchor.scalarMultiply(- 1))
        }


        // const edges = [...this.edges.traverse(this.edges.indexOfValue(edge) + 1)]
        // for (const edge of edges) {

        // }
        // for (const { value: edge } of this.edges.traverse({ startNode: edge }))


        // for (const [rectangle] of this.#holes) {
        //     const { anchor, width, height } = rectangle.boundingBox
        //     const boundingRect = new Rectangle(anchor, width, height)
        //     const anchorCoord = edge.isVertical
        //         ? boundingRect.anchor.y
        //         : boundingRect.anchor.x
        //     if (anchorCoord > dividerCoord) {
        //         this.transformHole(rectangle, { translation: deltaVector })
        //     }
        // }

        if (dissolveVertices) { this.dissolveVertices() }
    }

    /**
     * 
     * @param {Rectangle} rectangle 
     */
    addHole(rectangle) {
        // Throw if hole already exists.
        if (this.#holes.has(rectangle)) {
            throw new RectilinearPolygonError({
                message: "Rectangle hole already exists in polygon",
                fix: "Do not add duplicate rectangle holes to polygon"
            })
        }

        // Throw if the hole is not entirely enclosed by current rectilinear
        // polygon instance.
        if (!this.enclosesPolygon(rectangle)) {
            throw new RectilinearPolygonError({
                message: "New hole is not enclosed by containing polygon",
                fix: "Add hole which is enclosed entirely by polygon instance"
            })
        }

        // Get inscribed hole for value of map object.
        const hole = rectangle.getInscribedRectangle({ integerVertices: true })

        this.#holes.set(rectangle, hole)
    }

    /**
     * 
     * @param {Rectangle} rectangle 
     * @param {object} obj
     * @param {number} [obj.resizeWidth]
     * @param {number} [obj.resizeHeight]
     * @param {Vector2D} [obj.translation]
     * @param {number} [obj.rotation]
     */
    transformHole(rectangle, {
        resizeWidth = rectangle.width,
        resizeHeight = rectangle.height,
        translation = new Vector2D(0, 0),
        rotation = 0
    } = {}) {
        // Throw if hole does not exist.
        if (!this.#holes.has(rectangle)) {
            throw new RectilinearPolygonError({
                message: "Hole for transform does not exist in polygon",
                fix: "Transform a hole which exists in polygon"
            })
        }

        // Test that transformed rectangle will still be *entirely* contained
        // within the current rectilinear polygon instance. Throw if not.
        const testTransform = rectangle
            .copy()
            .resize({ width: resizeWidth, height: resizeHeight })
            .translate(translation)
            .rotate(rotation)
        if (!this.enclosesPolygon(testTransform)) {
            throw new RectilinearPolygonError({
                message: "Hole transform intersects with containing polygon",
                fix: "Apply transform which does not cause intersection"
            })
        }

        // Transform existing rectangle as requested in argument parameters.
        rectangle
            .resize({ width: resizeWidth, height: resizeHeight })
            .translate(translation)
            .rotate(rotation)

        // Get inscribed hole for value of map object.
        const hole = rectangle.getInscribedRectangle({ integerVertices: true })

        this.#holes.set(rectangle, hole)
    }

    /**
     * 
     * @param {Rectangle} rectangle 
     */
    removeHole(rectangle) {
        // Throw if hole does not exist.
        if (!this.#holes.has(rectangle)) {
            throw new RectilinearPolygonError({
                message: "Rectangle hole does not exist in polygon",
                fix: "Delete a hole which exists in polygon"
            })
        }

        this.#holes.delete(rectangle)
    }

    /**
     * Get internal or external area of the border region of the current polygon
     * instance.
     * @param {number|number[]} widths Single border width, or array of border
     * widths for each edge in polygon.
     * @param {"internal"|"external"} mode Internal or external mode for border
     * area calculation.
     * @returns {number} Calculated border area.
     * @throws {RectilinearPolygonError} Throws when:
     * - Polygon is not closed.
     * - Number of given widths does not match number of edges in polygon.
     */
    getBorderArea(widths, mode) {
        // Throw if polygon not closed.
        if (!this.isClosed) {
            throw new RectilinearPolygonError({
                message: "Polygon not closed, cannot calculate border area",
                fix: "Request border area of a closed polygon"
            })
        }

        // 
        if (typeof widths === "number") {
            const width = widths
            widths = Array.from({ length: this.edges.size }, () => width)
        }
        if (widths.length !== this.edges.size) {
            throw new RectilinearPolygonError({
                message: "Number of border widths does not match edge count",
                fix: "Provide border widths for each edge in polygon"
            })
        }

        // 
        let area = 0
        for (const [index, edgeNode] of [...this.edges].entries()) {
            // 
            const borderWidth = widths[index]
            const nextBorderWidth = widths[index + 1] || widths[0]

            // 
            area += edgeNode.value.magnitude * borderWidth

            // 
            const vertex = /** @type {DoubleLinkedListNode.<Edge>} */
                (edgeNode.next).value.start
            const cornerArea = borderWidth * nextBorderWidth
            const angle = mode === "internal"
                ? this.internalAngleAtVertex(vertex)
                : this.externalAngleAtVertex(vertex)
            angle === Math.PI / 2 ? area -= cornerArea : area += cornerArea
        }

        return area
    }

    /**
     * Partition current *rectilinear* polygon instance into a series of
     * distinct, non-overlapping *rectangle* polygons.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.isCrossAxis] Should polygon be partitioned using
     * cross axis (vertical) dividing lines?
     * @param {boolean} [obj.shouldExpand] Should generated rectangles be
     * expanded to their maximum width/heigh without intersecting edges?
     * @param {boolean} [obj.inPlace] Should partition operation happen in
     * place? In place partition operations will modify the current rectilinear
     * polygon instance.
     * @returns {Rectangle[]} Array of rectangle instance which partition
     * current polygon instance.
     * @throws {RectilinearPolygonError} Throws when:
     * - Current polygon is not a closed shape.
     * - An anchor edge cannot be determined because edges aren't grid-aligned.
     */
    partitionPolygon({
        isCrossAxis = false,
        shouldExpand = true,
        inPlace = false
    } = {}) {
        // 
        if (!inPlace) {
            return this.copy().partitionPolygon({
                isCrossAxis,
                shouldExpand,
                inPlace: true
            })
        }

        // Throw if shape is not closed.
        if (!this.isClosed) {
            throw new RectilinearPolygonError({
                message: "Cannot partition incomplete polygon",
                fix: "Ensure that polygon is closed before attempting partition"
            })
        }

        // If only 4 edges remaining, return final rectangle.
        if (this.edges.size === 4 && !this.#holes.size) {
            const { anchor, width, height } = this.boundingBox
            return [new Rectangle(anchor, width, height)]
        }

        // 
        const axisAlignedEdges = isCrossAxis
            ? this.verticalEdges
            : this.horizontalEdges
        for (const [, hole] of this.#holes) {
            const edges = isCrossAxis
                ? hole.verticalEdges
                : hole.horizontalEdges
            axisAlignedEdges.push(...edges)
        }

        const anchorEdge = axisAlignedEdges.sort((edgeA, edgeB) => {
            return isCrossAxis
                ? edgeA.start.x - edgeB.start.x
                : edgeA.start.y - edgeB.start.y
        }).at(0)
        if (!anchorEdge) {
            throw new RectilinearPolygonError({
                message: "Cannot infer anchor edge",
                fix: "Ensure that sufficient grid aligned edges exist"
            })
        }
        for (const [rectangle, hole] of this.#holes) {
            for (const { value: holeEdge } of hole.edges) {
                if (anchorEdge.getParallelIntersections(holeEdge)) {
                    this.removeHole(rectangle)
                    this.#subtractEdgeRectangle(hole)
                    return this.partitionPolygon({
                        isCrossAxis,
                        shouldExpand,
                        inPlace
                    })
                }
            }
        }

        // 
        let sideEdgeMagnitude = 0
        EDGE_LOOP: for (const [index, edge] of axisAlignedEdges.entries()) {
            // 
            const shouldIgnore = isCrossAxis
                ? edge.start.x <= anchorEdge.start.x
                : edge.start.y <= anchorEdge.start.y
            if (shouldIgnore) { continue }

            // 
            const oppositeEdge = anchorEdge.copy()
            isCrossAxis
                ? oppositeEdge.start.set({ x: edge.start.x })
                : oppositeEdge.start.set({ y: edge.start.y })

            sideEdgeMagnitude = isCrossAxis
                ? oppositeEdge.start.x - anchorEdge.start.x
                : oppositeEdge.start.y - anchorEdge.start.y

            // 
            for (const edge of axisAlignedEdges.slice(index)) {
                const intersections = oppositeEdge
                    .getParallelIntersectionFractions(edge)
                if (!intersections) { continue }

                // 
                const { start: startFraction, end: endFraction } = intersections
                if (startFraction > 1 || endFraction < 0) { continue }
                else if (startFraction === 1 || endFraction === 0) {
                    if (!shouldExpand) { break EDGE_LOOP }
                }
                else if (startFraction < 1 || endFraction > 0) {
                    break EDGE_LOOP
                }
            }
        }

        // 
        const anchor = anchorEdge.x < 0 || anchorEdge.y < 0
            ? anchorEdge.end.copy()
            : anchorEdge.start.copy()
        const [width, height] = isCrossAxis
            ? [sideEdgeMagnitude, anchorEdge.magnitude]
            : [anchorEdge.magnitude, sideEdgeMagnitude]
        const rectangle = new Rectangle(anchor, width, height)
        const rectangles = [rectangle]

        // 
        const polygons = this.#subtractZoneRectangle(rectangle)
        polygons.forEach(polygon => {
            rectangles.push(...polygon.partitionPolygon(...arguments))
        })

        return rectangles
    }

    /**
     * Copy current rectilinear polygon instance, returning a new instance
     * containing copies of all of the same vertices and edges etc. such that no
     * object references are shared/duplicated between the two instances.
     * @returns {RectilinearPolygon} New rectilinear polygon instance copied
     * from current instance.
     */
    copy() {
        // Make copies of all vertex and edge node *values*, ensuring also that
        // edge copies have the correct copied start vertex reference.
        const [vertices, edges] = /** @type {[Vector2D[], Edge[]]} */ ([[], []])
        for (const [index, vertex] of this.vertices.array.entries()) {
            const edge = this.edges.array[index]
            const vertexCopy = vertex.copy()
            vertices.push(vertexCopy)
            edges.push(edge.copy(vertexCopy))
        }

        // 
        const holes = /** @type {Map.<Rectangle, Rectangle>} */ (new Map())
        for (const [rectangle, hole] of this.#holes) {
            holes.set(rectangle.copy(), hole.copy())
        }

        // Construct new rectilinear polygon from copied vertex and edge data
        // arrays.
        const { isClosed, isLocked, rotation } = this
        const rectilinearPolygon = RectilinearPolygon.from(
            new DoubleLinkedList(vertices, { isClosed }),
            new DoubleLinkedList(edges, { isClosed }),
            { isLocked, rotation, holes }
        )

        return rectilinearPolygon
    }

    /**
     * 
     */
    #closePolygon() {
        // Fetch first and previous edges in polygon, ignoring if not found.
        const [firstEdge, previousEdge] = [
            this.edges.head?.value,
            this.edges.tail?.value
        ]
        if (!firstEdge || !previousEdge) {
            throw new RectilinearPolygonError({

            })
        }

        // Fetch first and previous vertices in polygon, ignoring if not found.
        const [firstVertex, previousVertex] = [
            this.vertices.head?.value,
            this.vertices.tail?.value
        ]
        if (!firstVertex || !previousVertex) {
            throw new RectilinearPolygonError({
                
            })
        }

        // Define closing edge, and fetch required properties to check validity.
        const edge = new Edge(previousVertex, firstVertex)
        const edgeNode = new DoubleLinkedListNode(edge)
        const { isGridAligned, reverseDirection } = edge
        const { direction: previousDirection } = previousEdge
        const { direction: firstDirection } = firstEdge

        // Ignore if edge direction is not grid aligned, or if the edge is back
        // along first or previous edges.
        if (
            !isGridAligned ||
            reverseDirection === firstDirection ||
            reverseDirection === previousDirection
        ) {
            throw new RectilinearPolygonError({
                
            })
        }

        // Check that edge does not intersect any others, ignoring first edge
        // and previous edge.
        for (const edgeNode of this.edges) {
            if (edgeNode === this.edges.head || edgeNode === this.edges.tail) {
                continue
            }
            if (edge.intersects(edgeNode.value)) {
                throw new RectilinearPolygonError({
                
                })
            }
        }

        // Push closing edge, close internal linked lists, and dissolve
        // redundant vertices.
        this.edges.addNode(edgeNode)
        this.edges.close()
        this.vertices.close()
        this.dissolveVertices()
    }

    /**
     * 
     * @param {Rectangle} rectangle 
     */
    #subtractEdgeRectangle(rectangle) {
        // Subtract each rectangle edge from the current polygon instance, and
        // any new, orphaned polygon instances resulting from previous edge
        // subtractions.
        for (const { value: rectangleEdge } of rectangle.edges) {
            this.subtractEdge(rectangleEdge)
        }

        for (const vertex of rectangle.vertices.array) {
            const index = this.vertices.findNode(node => {
                return node.value.equals(vertex)
            })
            if (index) { continue }

            try { this.addVertex(vertex) }
            catch {
                const { x, y } = this.vertices.tail?.value || {}
                let corner = new Vector2D(vertex.x, y)

                const headVertex = this.vertices.head?.value
                if (headVertex && corner.equals(headVertex)) {
                    corner = new Vector2D(x, vertex.y)
                }

                this.addVertex(corner)
                this.addVertex(vertex)
            }
        }

        const headVertex = this.vertices.head?.value
        if (headVertex) { this.addVertex(headVertex) }
    }

    /**
     * Subtract a *rectangle* polygon from the current *rectilinear* polygon
     * instance. Note that this method is *not* a complete implementation of a
     * polygon boolean subtract method. For more information on implementing or
     * using boolean operations on polygons in javascript, see the repository
     * {@link https://github.com/velipso/polybooljs|here}.
     * @param {Rectangle} rectangle Rectangle polygon to subtract. Note that
     * this rectangle should fill a "zone" of the rectilinear polygon (i.e. should be
     * expanded to an opposite edge ) 
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.filterRoot] Should filter current polygon instance
     * from returned array of polygons?
     * @returns {RectilinearPolygon[]} Array of resulting rectilinear polygon
     * instances following removal.
     * @see {@link https://github.com/velipso/polybooljs}
     */
    #subtractZoneRectangle(rectangle, { filterRoot = false } = {}) {
        const polygons = /** @type {(Polygon|RectilinearPolygon)[]} */ ([this])

        // Subtract each rectangle edge from the current polygon instance, and
        // any new, orphaned polygon instances resulting from previous edge
        // subtractions.
        for (const { value: rectangleEdge } of rectangle.edges) {
            for (const polygon of polygons) {
                polygons.push(...polygon.subtractEdge(rectangleEdge))
            }
        }

        // 
        const rectilinearPolygons = polygons
            .filter(polygon => polygon.vertices.size >= 4)
            .flatMap(polygon => this.#rectifySplitPolygon(polygon, rectangle))

        return filterRoot
            ? rectilinearPolygons.filter(polygon => polygon !== this)
            : rectilinearPolygons
    }

    /**
     * Close a rectilinear polygon following multiple edge subtractions. By
     * default this method will simply close the polygon by calling the private
     * #closePolygon method. Note that when the closing edge is not grid aligned
     * (i.e. where the subtracted part of the polygon does not leave open
     * vertices which are grid aligned):
     * - New corner vertex between `head` and `tail` nodes inferred from the
     * vertices of the subtracted rectangle (i.e. ). 
     * - If inferred corner vertex already exists in polygon, split the polygon
     * at this vertex node, and close both new polygons.
     * - Otherwise if inferred corner vertex does *not* exist in polygon, add it
     * as a new vertex between `head` and `tail` nodes, creating *two* grid
     * aligned edges between the `head` and `tail` vertices in order to close
     * the shape.
     * @param {RectilinearPolygon|Polygon} splitPolygon Split polygon to close
     * or subdivide.
     * @param {Rectangle} rectangle Subtracted rectangle which resulted in
     * current split polygon.
     * @returns {RectilinearPolygon[]} Resulting array of closed rectilinear
     * polygons.
     * @throws {RectilinearPolygonError} Throws when:
     * - Corner vertex cannot be inferred from subtracted rectangle in order to
     * correctly close or subdivide polygon.
     */
    #rectifySplitPolygon(splitPolygon, rectangle) {
        // 
        const { vertices: splitVertices, edges: splitEdges } = splitPolygon
        const polygon = splitPolygon instanceof RectilinearPolygon
            ? splitPolygon
            : RectilinearPolygon.from(splitVertices, splitEdges)
        const polygons = [polygon]

        // 
        if (!splitVertices.head || !splitVertices.tail) { return [polygon] }
        const { head: { value: start }, tail: { value: end } } = splitVertices

        if (!new Edge(start, end).isGridAligned) {
            const cornerA = new Vector2D(start.x, end.y)
            const cornerB = new Vector2D(end.x, start.y)

            const rectangleCornerNode = rectangle.vertices
                .findNode(({ value: vertex }) => {
                    return vertex.equals(cornerA) || vertex.equals(cornerB)
                })
            if (!rectangleCornerNode) {
                throw new RectilinearPolygonError({

                })
            }

            const existingCornerNode = splitVertices
                .findNode(({ value: vertex }) => {
                    return vertex.equals(rectangleCornerNode?.value)
                })

            if (existingCornerNode) {
                const index = splitVertices.indexOfNode(existingCornerNode)

                const vertex = existingCornerNode.value.copy()
                const vertexNode = new DoubleLinkedListNode(vertex)
                splitVertices.addNode(vertexNode, { parentNode: index })

                const vertices = splitVertices.splitAtNode(index)
                const edges = splitEdges.splitAtNode(index - 1)
                if (vertices && edges) {
                    polygons.push(RectilinearPolygon.from(vertices, edges))
                }
            }
            else { polygon.addVertex(rectangleCornerNode?.value) }
        }

        for (const polygon of polygons) {
            const headVertex = polygon.vertices.head?.value
            if (headVertex) {
                try { polygon.addVertex(headVertex) }
                catch {
                    const { x, y } = polygon.vertices.tail?.value || {}
                    let corner = new Vector2D(headVertex.x, y)

                    if (corner.equals(headVertex)) {
                        corner = new Vector2D(x, headVertex.y)
                    }
                    polygon.addVertex(corner)
                    polygon.addVertex(headVertex)
                }
            }
            polygon.dissolveVertices()
        }

        return polygons
    }

    /**
     * Generate new rectilinear polygon instance from vertex and edge linked
     * list instances and other optional class properties.
     * @param {DoubleLinkedList.<Vector2D>} vertices Linked list of vertices to
     * generate polygon with.
     * @param {DoubleLinkedList.<Edge>} edges Linked list of edges to generate
     * polygon with.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.isLocked] Is new instance locked?
     * @param {number} [obj.rotation] Starting rotation of new instance.
     * @param {Map.<Rectangle, Rectangle>} [obj.holes] Holes map to be added to
     * new instance.
     * @returns {RectilinearPolygon} New rectilinear polygon instance with given
     * vertex and edge linked lists set.
     */
    static from(vertices, edges, {
        isLocked = false,
        rotation = 0,
        holes = new Map()
    } = {}) {
        // Create new rectilinear polygon instance from given vertex and edge
        // linked lists.
        const polygon = new RectilinearPolygon()
        polygon.vertices = vertices
        polygon.edges = edges

        // Update optional private properties of new instance.
        polygon.isLocked = isLocked
        polygon.rotation = rotation
        polygon.#holes = holes

        return polygon
    }

    /**
     * 
     * @param {number[]} flatVertexArray 
     * @returns {RectilinearPolygon}
     */
    static fromFlatArray(flatVertexArray) {
        if (flatVertexArray.length % 2 !== 0) {
            throw new Error() 
        }

        const polygon = new RectilinearPolygon()
        while (flatVertexArray.length) {
            polygon.addVertex(new Vector2D(...flatVertexArray.splice(2)))
        }

        return polygon
    }

    get holes() {
        return this.#holes // convert to array of holes? 
    }
}

/**
 * 
 */
class Rectangle extends RectilinearPolygon {
    #width; #height

    /**
     * Construct rectangle declared using width, height and anchor point.
     * @param {Vector2D} anchor Anchor vector at top left corner of rectangle.
     * @param {number} width Scalar width of rectangle.
     * @param {number} height Scalar height of rectangle.
     */
    constructor(anchor, width, height) {
        // Ensure that width and height of rectangle are both positive. Rectify
        // anchor as required if input width or height is negative.
        if (width < 0) {
            anchor.set({ x: anchor.x + width })
            width *= - 1
        }
        if (height < 0) {
            anchor.set({ y: anchor.y + height })
            height *= - 1
        }

        // Construct rectangle vertices.
        const { x, y } = anchor
        const vertices = [
            anchor.copy(),
            new Vector2D(x + width, y),
            new Vector2D(x + width, y + height),
            new Vector2D(x, y + height)
        ]

        super({ vertices, isClosed: true })

        // Initialise rectangle properties, and lock polygon for alteration.
        this.#width = width
        this.#height = height
        this.isLocked = true
    }

    /**
     * 
     * @param {object} obj
     * @param {number} [obj.width]
     * @param {number} [obj.height] 
     * @param {boolean} [obj.inPlace]
     * @returns {Rectangle}
     */
    resize({
        width = this.#width,
        height = this.#height,
        inPlace = true
    } = {}) {
        const polygon = inPlace ? this : this.copy()

        // 
        if (width !== polygon.#width || height !== polygon.#height) {
            for (const node of polygon.edges) {
                // 
                const index = polygon.edges.indexOfNode(node)
                const scalar = index % 2
                    ? height / polygon.#height
                    : width / polygon.#width
                node.value.scalarMultiply(scalar, { inPlace: true })

                node.next?.value.start.set(node.value.end)
            }
        }

        // 
        polygon.#updateWidthHeight()

        return polygon
    }

    /**
     * Generate
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.integerVertices] Should vertices of the
     * generated inscribed rectangle be rounded to integer values? Note that any
     * rounding will be performed in the *correct* sense such that the new
     * inscribed rectangle is entirely contained by the current rectangle
     * instance.
     * @returns {Rectangle} New rectangle instance which can be inscribed within
     * the current containing rectangle instance.
     */
    getInscribedRectangle({ integerVertices = false } = {}) {
        const {
            base: baseEdgeMagnitude,
            side: sideEdgeMagnitude
        } = this.#width > this.#height
            ? { base: this.#height, side: this.#width }
            : { base: this.#width, side: this.#height }
        const baseEdge = baseEdgeMagnitude === this.#width
            ? this.edges.array[0]
            : this.edges.array[1]

        const { rotation, center } = this
        const halfSideEdge = sideEdgeMagnitude / 2
        const elevation = Math.PI / 2 - Math.abs(baseEdge.direction)
        let theta = elevation
        let halfDiagonal = baseEdgeMagnitude / (2 * Math.sin(elevation + theta))
        while (
            Math.abs(halfDiagonal * Math.cos(elevation - theta)) > halfSideEdge
        ) {
            theta += (rotation > Math.PI / 4 && rotation < 2 * Math.PI / 4) ||
                (rotation > 3 * Math.PI / 4 && rotation < 5 * Math.PI / 4) ||
                (rotation > 6 * Math.PI / 4 && rotation < 7 * Math.PI / 4)
                    ? 0.001 : - 0.001
            halfDiagonal = baseEdgeMagnitude / (2 * Math.sin(elevation + theta))
        }

        const width = integerVertices
            ? Math.floor(2 * halfDiagonal * Math.cos(theta))
            : 2 * halfDiagonal * Math.cos(theta)
        const height = integerVertices
            ? Math.floor(2 * halfDiagonal * Math.sin(theta))
            : 2 * halfDiagonal * Math.sin(theta)

        const anchorX = integerVertices
            ? Math.ceil(center.x - width / 2)
            : center.x - width / 2
        const anchorY = integerVertices
            ? Math.ceil(center.y - height / 2)
            : center.y - height / 2
        const anchor = new Vector2D(anchorX, anchorY)

        return new Rectangle(anchor, width, height)
    }

    /**
     * 
     * @param {object} obj
     * @param {number} [obj.start]
     * @param {number} [obj.end]
     * @param {boolean} [obj.flipped]
     * @returns {Rectangle} 
     */
    getSubRectangle({ start = 0, end = 0, flipped = false } = {}) {
        const fraction = end - start
        if (fraction < 0) {
            throw new RectangleError({
                message: "Negative fraction requested as sub-rectangle",
                fix: "Request non-negative fraction of rectangle",
                "start-fraction": start,
                "end-fraction": end
            })
        }

        const { anchor: { x, y }, width, height } = this
        const anchor = flipped
            ? new Vector2D(x, y + height * start)
            : new Vector2D(x + width * start, y)

        return flipped
            ? new Rectangle(anchor, width, height * fraction)
            : new Rectangle(anchor, width * fraction, height)
    }

    /**
     * Copy current rectangle polygon instance, returning a new instance
     * containing copies of all of the same vertices and edges etc. such that no
     * object references are shared/duplicated between the two instances.
     * @returns {Rectangle} New rectangle polygon instance copied from current
     * instance.
     */
    copy() {
        // Make copies of all vertex and edge node *values*, ensuring also that
        // edge copies have the correct copied start vertex reference.
        const [vertices, edges] = /** @type {[Vector2D[], Edge[]]} */ ([[], []])
        for (const [index, vertex] of this.vertices.array.entries()) {
            const edge = this.edges.array[index]
            const vertexCopy = vertex.copy()
            vertices.push(vertexCopy)
            edges.push(edge.copy(vertexCopy))
        }

        // Construct new rectilinear polygon from copied vertex and edge data
        // arrays.
        const { isClosed, isLocked, rotation } = this
        const rectangle = Rectangle.from(
            new DoubleLinkedList(vertices, { isClosed }),
            new DoubleLinkedList(edges, { isClosed }),
            { width: this.#width, height: this.#height, isLocked, rotation }
        )

        return rectangle
    }

    /**
     * 
     * @returns {number[]}
     */
    toFlatArray() {
        return [this.anchor.x, this.anchor.y, this.width, this.height]
    }

    /**
     * Update internal width and height properties based on relevant edges in
     * rectangle. Note that the width and height properties are aligned directly
     * to the rectangle, and *not* to the grid.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.tolerance] Minimum width or height change required
     * for update to be reflected to internal properties.
     */
    #updateWidthHeight({ tolerance = 0 } = {}) {
        // Infer width and height of rectangle from magnitude of relevant edges.
        const width = this.edges.array[0].magnitude
        const height = this.edges.array[1].magnitude

        // Calculate absolute differences of dimensions compared to existing
        // values.
        const widthDiff = Math.abs(this.#width - width)
        const heightDiff = Math.abs(this.#height - height)

        // Update dimensions if difference exceeds required tolerance.
        if (widthDiff > tolerance) { this.#width = width }
        if (heightDiff > tolerance) { this.#height = height }
    }

    /**
     * Generate new rectangle polygon instance from vertex and edge linked
     * list instances and other optional class properties.
     * @param {DoubleLinkedList.<Vector2D>} vertices Linked list of vertices to
     * generate polygon with.
     * @param {DoubleLinkedList.<Edge>} edges Linked list of edges to generate
     * polygon with.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D} [obj.anchor] Top left corner anchor vertex of rectangle
     * with default inferred from head node of vertex linked list.
     * @param {number} [obj.width] Width of rectangle with default inferred from
     * given edges.
     * @param {number} [obj.height] Height of rectangle with default inferred
     * from given edges.
     * @param {boolean} [obj.isLocked] Is new instance locked?
     * @param {number} [obj.rotation] Starting rotation of new instance.
     * @returns {Rectangle} New rectilinear polygon instance with given
     * vertex and edge linked lists set.
     * @throws {RectangleError} Throws when:
     * - Vertex and/or edge lists provided are not correctly sized for
     * generating a rectangle (i.e. should be size 4).
     */
    static from(vertices, edges, {
        anchor = vertices.head?.value || new Vector2D(0, 0),
        width,
        height,
        isLocked = true,
        rotation = 0
    } = {}) {
        // Reject if vertices or edges provided do not form a rectangle.
        if (vertices.size !== 4 || edges.size !== 4) {
            throw new RectangleError({
                message: "Vertex and/or edge lists provided are not of size 4",
                fix: "Create rectangle from suitable vertex and edge lists",
                "vertex-list-size": vertices.size,
                "edge-list-size": edges.size
            })
        }

        // Infer rectangle width and height if unset.
        width ??= edges.array[0].magnitude
        height ??= edges.array[1].magnitude

        // Create new rectilinear polygon instance from given vertex and edge
        // linked lists. 
        const rectangle = new Rectangle(anchor, width, height)

        // Update optional private properties of new instance.
        rectangle.isLocked = isLocked
        rectangle.rotation = rotation

        //
        rectangle.vertices = vertices
        rectangle.edges = edges

        return rectangle
    }

    /**
     * 
     * @param {number[]} flatArray 
     * @returns {Rectangle}
     */
    static fromFlatArray(flatArray) {
        if (flatArray.length !== 4) {
            throw new Error() 
        }

        const [x, y, width, height] = flatArray
        const anchor = new Vector2D(x, y)
        const polygon = new Rectangle(anchor, width, height)

        return polygon
    }

    /**
     * 
     * @param {string} serialized 
     * @returns {Rectangle}
     */
    static fromSerialized(serialized) {
        return Rectangle.fromFlatArray(JSON.parse(atob(serialized)))
    }

    get center() {
        return this.anchor
            .add(this.edges.array[0].scalarMultiply(0.5))
            .add(this.edges.array[1].scalarMultiply(0.5))
    }

    get anchor() {
        return this.vertices.array[0]
    }

    get width() {
        return this.#width
    }

    get height() {
        return this.#height
    }
}

/**
 * Named rectilinear polygon error.
 */
class RectilinearPolygonError extends DecoratedError {
    /**
     * Construct linked list named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "RectilinearPolygonError"
    }
}

/**
 * Named rectangle error.
 */
class RectangleError extends DecoratedError {
    /**
     * Construct linked list node named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "RectangleError"
    }
}

// @@exports
export { RectilinearPolygon, Rectangle }
